;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:propstrata-app":true});}catch(e){}}();import Fuse from "fuse.js";
import {
	TOUR_CATEGORY_LABELS,
	TOUR_CATEGORY_ORDER,
} from "@/features/onboarding-tours/types/tour-category";
import type {
	TourDefinition,
	TourEligibilityContext,
} from "@/features/onboarding-tours/types/tour-definition";

export const TOURS_PER_PAGE = 8;

export interface CatalogRow {
	id: TourDefinition["id"];
	title: string;
	description: string;
	category: TourDefinition["category"];
	categoryLabel: string;
	isLocked: boolean;
	lockedLabel?: string;
}

export interface CatalogPageResult {
	rows: CatalogRow[];
	page: number;
	totalPages: number;
	totalCount: number;
}

function isTourLocked({
	tour,
	eligibilityContext,
}: {
	tour: TourDefinition;
	eligibilityContext: TourEligibilityContext;
}): boolean {
	if (
		tour.eligibility.requiresFullProductAccess &&
		!eligibilityContext.hasFullProductAccess
	) {
		return true;
	}
	if (
		tour.eligibility.requiresInvitePermission &&
		!eligibilityContext.canInvite
	) {
		return true;
	}
	if (
		tour.eligibility.requiresDataProviderDelete &&
		!eligibilityContext.canDeleteDataProvider
	) {
		return true;
	}
	if (
		tour.eligibility.requiresLeaderboardEdit &&
		!eligibilityContext.canEditLeaderboard
	) {
		return true;
	}
	if (
		tour.eligibility.requiresDashboardEdit &&
		!eligibilityContext.canEditDashboards
	) {
		return true;
	}
	if (
		tour.eligibility.requiresDashboardManage &&
		!eligibilityContext.canManageDashboardViews
	) {
		return true;
	}
	return false;
}

export function isTourStartBlockedForUser({
	tour,
	eligibilityContext,
}: {
	tour: TourDefinition;
	eligibilityContext: TourEligibilityContext;
}): boolean {
	return isTourLocked({ eligibilityContext, tour });
}

function isTourEligible({
	tour,
}: {
	tour: TourDefinition;
	eligibilityContext: TourEligibilityContext;
}): boolean {
	if (tour.catalog.hidden) {
		return false;
	}
	return true;
}

function sortTours(tours: TourDefinition[]): TourDefinition[] {
	return [...tours].sort((left, right) => {
		const categoryDelta =
			TOUR_CATEGORY_ORDER.indexOf(left.category) -
			TOUR_CATEGORY_ORDER.indexOf(right.category);
		if (categoryDelta !== 0) {
			return categoryDelta;
		}
		return left.title.localeCompare(right.title);
	});
}

function searchTours({
	tours,
	query,
}: {
	tours: TourDefinition[];
	query: string;
}): TourDefinition[] {
	const trimmed = query.trim();
	if (!trimmed) {
		return tours;
	}

	const fuse = new Fuse(tours, {
		keys: ["title", "description", "tags"],
		threshold: 0.35,
	});

	return fuse.search(trimmed).map((result) => result.item);
}

function paginateRows({
	rows,
	page,
	pageSize,
}: {
	rows: CatalogRow[];
	page: number;
	pageSize: number;
}): CatalogPageResult {
	const totalCount = rows.length;
	const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
	const safePage = Math.min(Math.max(page, 0), totalPages - 1);
	const start = safePage * pageSize;

	return {
		page: safePage,
		rows: rows.slice(start, start + pageSize),
		totalCount,
		totalPages,
	};
}

export function getCatalogPage({
	tours,
	query,
	page,
	pageSize = TOURS_PER_PAGE,
	eligibilityContext,
}: {
	tours: TourDefinition[];
	query: string;
	page: number;
	pageSize?: number;
	eligibilityContext: TourEligibilityContext;
}): CatalogPageResult {
	const visible = sortTours(
		tours.filter((tour) => isTourEligible({ eligibilityContext, tour })),
	);
	const matched = searchTours({ query, tours: visible });
	const rows: CatalogRow[] = matched.map((tour) => {
		const locked = isTourLocked({ eligibilityContext, tour });
		return {
			category: tour.category,
			categoryLabel: TOUR_CATEGORY_LABELS[tour.category],
			description: tour.description,
			id: tour.id,
			isLocked: locked,
			lockedLabel: locked ? tour.catalog.lockedLabel : undefined,
			title: tour.title,
		};
	});

	return paginateRows({ page, pageSize, rows });
}
